import { useEffect, useRef, useState } from "react";
import "./RecommendedItemsBar.css";
import { loadProducts } from "../../../../ProductsHandler.js";
import RecommendedItem from "./sub/RecommendedItem/RecommendedItem.jsx";
import { useNavigate } from "react-router-dom";

/**
 * Mobile-first fix:
 * - Phones/tablets use native horizontal scrolling (overflow-x: auto + momentum).
 * - Desktop keeps a "grab to scroll" UX by updating scrollLeft (NOT transforms).
 *   This avoids jank on iOS and works on all browsers.
 */
export default function RecommendedItemsBar() {
    const [recommendedItems, setRecommendedItems] = useState([]);
    const [dragging, setDragging] = useState(false);

    const navigate = useNavigate();
    const scrollerRef = useRef(null);

    // desktop drag state
    const isDraggingRef = useRef(false);
    const movedRef = useRef(false);
    const startXRef = useRef(0);
    const startScrollLeftRef = useRef(0);
    const rafIdRef = useRef(0);

    // suppress clicks right after native scroll on touch
    const nativeScrollingRef = useRef(false);
    const nativeScrollTORef = useRef(null);

    useEffect(() => {
        (async () => {
            const data = await loadProducts("name-desc", [], [], null, null, null, [0, 9999]);
            let items = data?.products ?? [];
            for (let i = items.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [items[i], items[j]] = [items[j], items[i]];
            }
            setRecommendedItems(items);
        })();
    }, []);

    // ----- Native scroll helpers (touch) -----
    const onNativeScroll = () => {
        nativeScrollingRef.current = true;
        if (nativeScrollTORef.current) clearTimeout(nativeScrollTORef.current);
        nativeScrollTORef.current = setTimeout(() => {
            nativeScrollingRef.current = false;
        }, 120);
    };

    // ----- Desktop "grab to scroll" (mouse/pen only) -----
    const isPointerDragEligible = (e) => e.pointerType === "mouse" || e.pointerType === "pen";

    const onPointerDown = (e) => {
        if (!isPointerDragEligible(e)) return; // let touch devices scroll natively
        const el = scrollerRef.current;
        if (!el) return;

        isDraggingRef.current = true;
        movedRef.current = false;
        startXRef.current = e.clientX;
        startScrollLeftRef.current = el.scrollLeft;
        setDragging(true);

        el.setPointerCapture?.(e.pointerId);
    };

    const onPointerMove = (e) => {
        if (!isPointerDragEligible(e)) return;
        if (!isDraggingRef.current) return;
        const el = scrollerRef.current;
        if (!el) return;

        const dx = e.clientX - startXRef.current;
        if (Math.abs(dx) > 4) movedRef.current = true;

        // rAF throttle for smoothness
        if (rafIdRef.current) return;
        rafIdRef.current = requestAnimationFrame(() => {
            el.scrollLeft = startScrollLeftRef.current - dx * 1.2; // speed factor
            rafIdRef.current = 0;
        });
    };

    const onPointerUp = (e) => {
        if (!isPointerDragEligible(e)) return;
        const el = scrollerRef.current;
        el?.releasePointerCapture?.(e.pointerId);
        isDraggingRef.current = false;
        setDragging(false);
        // (No JS momentum; native scroll momentum still works on trackpads)
        setTimeout(() => (movedRef.current = false), 120);
    };

    // Convert vertical wheel to horizontal scroll for desktop mice
    const onWheel = (e) => {
        const el = scrollerRef.current;
        if (!el) return;
        const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
        if (!delta) return;
        e.preventDefault();
        el.scrollLeft += delta;
    };

    const handleItemClick = (item, e) => {
        // Suppress click if user was scrolling
        if (nativeScrollingRef.current || movedRef.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        const productPath = item.handle
            ? `/produkt/${item.handle}`
            : `/produkt?id=${encodeURIComponent(item.id)}`;
        navigate(productPath);
    };

    return (
        <div id="recommended-items-bar">
            <div
                id="recommended-items-mover"
                ref={scrollerRef}
                className={dragging ? "dragging" : ""}
                onScroll={onNativeScroll}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={onPointerUp}
                onWheel={onWheel}
            >
                <div className="recommended-items-track">
                    {recommendedItems.map((item, idx) => (
                        <RecommendedItem
                            key={item.id ?? idx}
                            item={item}
                            onClick={(e) => handleItemClick(item, e)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
